import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import Seo from "../../components/Seo";
import { EmptyState, PageHeader, Spinner } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, lawyerService } from "../../services/lawyerApi";

const fmt = (v) => Number((Number(v) || 0).toFixed(2)).toLocaleString();

const LawyerCallPricing = () => {
  const { profile } = useOutletContext();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    lawyerService
      .getCallPackages()
      .then((res) => {
        if (!active) return;
        if (!res?.data?.status) throw new Error(res?.data?.message);
        setPlans(res.data.data || []);
      })
      .catch((err) => active && setError(apiErrorMessage(err, err?.message || "Unable to load call plans.")))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const commission = Number(profile?.partner_commission);

  return (
    <div className="lwp-page lwp-page-narrow">
      <Seo title="Call Plans & Earnings | Legal Platform" noindex />
      <PageHeader title="Call Plans & Earnings" backTo="/lawyer/settings" />
      <p className="lwp-muted lwp-plans-lead">See how much you earn on each call plan after platform fee.</p>

      {loading ? (
        <Spinner />
      ) : error ? (
        <EmptyState icon="bi-exclamation-circle" text={error} />
      ) : plans.length === 0 ? (
        <EmptyState icon="bi-telephone" text="No call plans are available right now." />
      ) : (
        <ul className="lwp-plan-list">
          {plans.map((plan) => {
            const free = plan.is_free || Number(plan.price) === 0;
            const payout = free ? 0 : Math.max(0, Number(plan.price) - Number(plan.commission_amount || 0));
            const showTitle = plan.title && !/^\d+\s*min/i.test(plan.title);
            return (
              <li key={plan.id} className={`lwp-plan${free ? " featured" : ""}`}>
                <div className="lwp-plan-top">
                  <span className="lwp-plan-icon">
                    <i className="bi bi-clock" />
                  </span>
                  <div className="lwp-plan-name">
                    <strong>
                      {plan.duration_minutes} Minutes
                      {showTitle && !free && <small> ({plan.title})</small>}
                    </strong>
                    {free && <span>Free Plan</span>}
                    {plan.is_popular && !free && <span className="lwp-plan-popular">Most popular</span>}
                  </div>
                  <span className="lwp-plan-price">{free ? "Free" : `${plan.currency || "USD"} ${fmt(plan.price)}`}</span>
                </div>
                <div className="lwp-plan-bottom">
                  <span>You will be paid</span>
                  <b>${fmt(payout)}</b>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && !error && Number.isFinite(commission) && commission > 0 && (
        <p className="lwp-plans-note">
          <i className="bi bi-info-circle" /> A {fmt(commission)}% platform fee is deducted from each paid consultation.
        </p>
      )}
    </div>
  );
};

export default LawyerCallPricing;
